// SPDX-License-Identifier: AGPL-3.0-only
import { download, request } from './client';

/**
 * The observability API.
 *
 * # THE ONE RULE THIS MODULE EXISTS TO PROTECT
 *
 * `state` and `last_seen_at` answer DIFFERENT QUESTIONS, and nothing in the
 * browser may turn the second into the first:
 *
 *     state         is this Gateway connected RIGHT NOW
 *     last_seen_at  when did the Server last hear from it
 *
 * The Server decides `state` from its live session registry — the only thing
 * that knows whether a socket is open. A row cannot know that, and a timestamp
 * five seconds old means "was alive five seconds ago", which becomes a lie
 * during exactly the outage an operator is investigating.
 *
 * So there is deliberately no helper here that computes connectivity from a
 * timestamp, and no threshold constant for one to be built from. If a function
 * ever appears in this file that takes `last_seen_at` and returns something
 * like `isOnline`, the distinction has been undone in the one place nobody
 * would think to look.
 */

/**
 * Where a Gateway stands.
 *
 * Five states rather than a boolean, because a boolean conceals the difference
 * between "it is broken" and "it was never set up", and those need opposite
 * actions from an operator.
 */
export type GatewayState =
  /** An authenticated session exists now and its heartbeat is healthy. */
  | 'ONLINE'
  /** A session exists but the heartbeat is late. Look at it. */
  | 'DEGRADED'
  /** No session, and there has been one before. */
  | 'OFFLINE'
  /** Administratively revoked. Wins over a live session. */
  | 'REVOKED'
  /** No session has ever been established. Nobody started it. */
  | 'NEVER_CONNECTED';

export interface GatewayStatus {
  gateway_id: string;
  state: GatewayState;

  /**
   * When this answer was true.
   *
   * A Gateway can disconnect between the lookup and the reply and nothing can
   * prevent that. This is what stops a client rendering a second-old answer as
   * a current one.
   */
  observed_at: string;

  /** Present only while a session exists. */
  connected_since?: string;
  last_heartbeat?: string;

  /** What the Gateway REPORTS it is running. Not verified by anything. */
  agent_version?: string;

  /** The persisted history. Present whatever the state. */
  last_seen_at?: string;
  last_connected_at?: string;
  last_disconnected_at?: string;
  last_disconnect_reason?: string;
}

/** The closed event vocabulary, as the Server defines it. */
export type EventCategory = 'operational' | 'transcript';
export type EventSeverity = 'INFO' | 'WARN' | 'ERROR';

export interface LogEvent {
  id: string;
  type: string;
  category: EventCategory;
  severity: EventSeverity;
  phase?: string;

  device_id?: string;
  gateway_id: string;
  job_id?: string;

  operation?: string;
  driver_id?: string;
  driver_revision?: string;

  duration_ms?: number;
  message?: string;

  /**
   * The transcript payload — what was sent, or what came back.
   *
   * NEVER A CREDENTIAL. A `device.tx.credential` event carries none: the
   * constructor that produces one has no text parameter, the Server refuses one
   * that arrives with a body, and a database constraint refuses it again. What
   * an operator sees in its place is the redaction marker in `message`.
   */
  body?: string;

  credential_kind?: string;
  truncated?: boolean;
  fields?: Record<string, string>;

  occurred_at: string;
}

export interface LogPage {
  items: LogEvent[];
  /** Absent when there is no further page. */
  next_cursor?: string;
  /** The page size ACTUALLY used, after the Server clamped it. */
  limit: number;
}

/**
 * What a caller may narrow a log read by.
 *
 * A closed set of named filters, not a query language. Every value is validated
 * by the Server against a closed vocabulary, so a typo is a refused request
 * rather than an empty page an operator reads as "my switch did nothing".
 */
export interface LogFilter {
  category?: EventCategory[];
  severity?: EventSeverity[];
  type?: string[];
  jobId?: string;
  since?: string;
  until?: string;
  limit?: number;
  cursor?: string;
}

function logQuery(filter: LogFilter): string {
  const params = new URLSearchParams();
  filter.category?.forEach((c) => params.append('category', c));
  filter.severity?.forEach((s) => params.append('severity', s));
  filter.type?.forEach((t) => params.append('type', t));
  if (filter.jobId) params.set('job_id', filter.jobId);
  if (filter.since) params.set('since', filter.since);
  if (filter.until) params.set('until', filter.until);
  if (filter.limit) params.set('limit', String(filter.limit));
  if (filter.cursor) params.set('cursor', filter.cursor);
  const q = params.toString();
  return q ? `?${q}` : '';
}

export const observabilityApi = {
  /**
   * One Gateway's current state and contact history.
   *
   * The tenant comes from the session. Supplying an id from another
   * organization returns 404 — the same answer an invented id gets, so this
   * cannot be used to discover which Gateways exist.
   */
  gatewayStatus: (gatewayId: string) =>
    request<GatewayStatus>(`/api/v1/gateways/${encodeURIComponent(gatewayId)}/status`),

  /** One device's timeline and transcript, newest first, bounded. */
  deviceLogs: (deviceId: string, filter: LogFilter = {}) =>
    request<LogPage>(`/api/v1/devices/${encodeURIComponent(deviceId)}/logs${logQuery(filter)}`),

  /** One Gateway's operational history, newest first, bounded. */
  gatewayLogs: (gatewayId: string, filter: LogFilter = {}) =>
    request<LogPage>(`/api/v1/gateways/${encodeURIComponent(gatewayId)}/logs${logQuery(filter)}`),

  /**
   * Download one device's history as a ZIP.
   *
   * # WHY THE DOWNLOAD TAKES NO FILTER
   *
   * The viewer's filters — severity, timeline-only — are for READING. An export
   * is evidence somebody will attach to a ticket and reason from, and one built
   * from whatever checkboxes happened to be set would omit exactly the events
   * that explain the failure, with nothing on screen to say so. The archive is
   * bounded by time and count and says so in metadata.json; it is not narrowed
   * by the browser's current view.
   *
   * The range is deliberately left to the Server's default. If a range is ever
   * offered here it must be an explicit choice in the download control, not an
   * inherited one.
   */
  downloadDeviceLogs: (deviceId: string) =>
    download(`/api/v1/devices/${encodeURIComponent(deviceId)}/logs/export`),

  /** Download one Gateway's history as a ZIP. Same reasoning as above. */
  downloadGatewayLogs: (gatewayId: string) =>
    download(`/api/v1/gateways/${encodeURIComponent(gatewayId)}/logs/export`),
};
